import { type Question } from '@/lib/types';
import { cn } from '@/lib/cn';

interface QuestionListProps {
  questions: Question[];
  answers: Record<string, string>;
  onAnswerChange: (questionId: string, answer: string) => void;
  isLoading?: boolean;
}

export default function QuestionList({
  questions,
  answers,
  onAnswerChange,
  isLoading,
}: QuestionListProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border p-4">
        <h3 className="font-medium mb-2">Clarifying Questions</h3>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Generating questions...
          </p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="rounded-lg border p-4">
        <h3 className="font-medium mb-2">Clarifying Questions</h3>
        <p className="text-sm text-muted-foreground">
          No additional information needed. Ready to generate draft!
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4">
      <h3 className="font-medium mb-3">Clarifying Questions</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Answer these questions to help generate a better response:
      </p>

      <div className="space-y-4">
        {questions.map((question) => (
          <QuestionItem
            key={question.id}
            question={question}
            answer={answers[question.id] || ''}
            onAnswerChange={(answer) => onAnswerChange(question.id, answer)}
          />
        ))}
      </div>
    </div>
  );
}

interface QuestionItemProps {
  question: Question;
  answer: string;
  onAnswerChange: (answer: string) => void;
}

function QuestionItem({ question, answer, onAnswerChange }: QuestionItemProps) {
  if (question.type === 'boolean') {
    return (
      <div>
        <label className="flex items-start gap-3">
          <div className="pt-0.5">
            {question.required && (
              <span className="text-red-500 text-sm mr-1">*</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-2">{question.text}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onAnswerChange('yes')}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  answer === 'yes'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                )}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => onAnswerChange('no')}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  answer === 'no'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                )}
              >
                No
              </button>
            </div>
          </div>
        </label>
      </div>
    );
  }

  if (question.type === 'choice' && question.choices) {
    return (
      <div>
        <label className="block">
          <div className="flex items-start gap-1 mb-2">
            {question.required && (
              <span className="text-red-500 text-sm">*</span>
            )}
            <p className="text-sm font-medium">{question.text}</p>
          </div>
          <select
            value={answer}
            onChange={(e) => onAnswerChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select an option...</option>
            {question.choices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  // Default to text input
  return (
    <div>
      <label className="block">
        <div className="flex items-start gap-1 mb-2">
          {question.required && <span className="text-red-500 text-sm">*</span>}
          <p className="text-sm font-medium">{question.text}</p>
        </div>
        <input
          type="text"
          value={answer}
          onChange={(e) => onAnswerChange(e.target.value)}
          placeholder="Type your answer..."
          className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </label>
    </div>
  );
}
